import type { TableProps } from "antd";
import { App } from "antd";
import { useCallback, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomTable, CustomTooltip } from "sipli-fleet-core/components";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import { DeleteIcon, EditIcon } from "src/assets/svgs";
import SharedEpochDate from "src/components/EpochDate/EpochDate";
import {
  useMutationDeleteTachographCard,
  useQueryGetTachographCardColumns,
  useQueryGetTachographCards,
} from "src/queries/tachographCard.queries";
import type {
  TachographCardPaginatedResponse,
  TachographCardRecord,
} from "src/services/tachographCardService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import { useEpochFormatter } from "../../../../sipli-fleet-core/components/EpochDate/useEpochFormatter";
import TachographCardAddEdit from "./TachographCardAddEdit";
import TachographCardDelete from "./TachographCardDelete";
import TachographCardStatusCell from "./TachographCardStatusCell";
import TachographCardConnectionCell from "./TachographCardConnectionCell";
import "./TachographCards.css";
import TachographCardsToolbar, { type TachographCardSortDirection } from "./TachographCardsToolbar";
interface TachographCardSorting {
  sortBy: string;
  sort: TachographCardSortDirection;
}

const Icon = (asset: unknown) => asset as ComponentType<{ className?: string }>;
const EpochDate = SharedEpochDate as ComponentType<{ date?: string | number | null }>;

const DeleteIconComponent = Icon(DeleteIcon);
const EditIconComponent = Icon(EditIcon);

const DEFAULT_PAGINATION = { page: 0, size: 10 };
const DEFAULT_SORTING: TachographCardSorting = {
  sortBy: "CARD_POSITION",
  sort: "DESC",
};

const COLUMN_IDS: Record<string, string> = {
  status: "STATUS",
  card: "CARD_ID",
  changedTimestamp: "CHANGED_TIMESTAMP",
  insertTimestamp: "INSERT_TIMESTAMP",
  position: "CARD_POSITION",
  expireDate: "EXPIRE_DATE",
  customer: "CUSTOMER",
};

const getTotalCount = (response: TachographCardPaginatedResponse | undefined, fallback: number) =>
  response?.totalItems ?? response?.totalElements ?? response?.total ?? fallback;

// const getOptionalDistributorId = (value: unknown) => {
//   const parsed = Number(value);
//   return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
// };

// const timeAgo = (timestamp: number) => {
//   const nowMs = Date.now();
//   const timeMs = timestamp > 1e12 ? timestamp : timestamp * 1000;
//   let diffMs = nowMs - timeMs;
//   const isFuture = diffMs < 0;
//   diffMs = Math.abs(diffMs);

//   const units = [
//     { name: "mo", ms: 2_592_000_000 },
//     { name: "w", ms: 604_800_000 },
//     { name: "d", ms: 86_400_000 },
//     { name: "h", ms: 3_600_000 },
//     { name: "m", ms: 60_000 },
//     { name: "s", ms: 1_000 },
//   ];

//   const result: string[] = [];

//   for (const unit of units) {
//     const count = Math.floor(diffMs / unit.ms);
//     if (count > 0) {
//       result.push(`${count}${unit.name}`);
//       diffMs %= unit.ms;
//     }
//     if (result.length === 2) break;
//   }

//   if (!result.length) return "now";
//   return isFuture ? `in ${result.join(" ")}` : `${result.join(" ")} ago`;
// };

const timeAgo = (timestamp: number, t: (key: string, options?: any) => string) => {
  const nowMs = Date.now();
  const timeMs = timestamp > 1e12 ? timestamp : timestamp * 1000;

  let diffMs = nowMs - timeMs;
  const isFuture = diffMs < 0;

  diffMs = Math.abs(diffMs);

  const units = [
    { name: "mo", ms: 2_592_000_000 },
    { name: "w", ms: 604_800_000 },
    { name: "d", ms: 86_400_000 },
    { name: "h", ms: 3_600_000 },
    { name: "m", ms: 60_000 },
    { name: "s", ms: 1_000 },
  ];

  const result: string[] = [];

  for (const unit of units) {
    const count = Math.floor(diffMs / unit.ms);

    if (count > 0) {
      result.push(`${count}${unit.name}`);
      diffMs %= unit.ms;
    }

    if (result.length === 2) break;
  }

  if (!result.length) {
    return t("time.now");
  }

  const value = result.join(" ");

  return isFuture ? t("time.in", { value }) : t("time.ago", { value });
};

const getSearchText = (record: TachographCardRecord) =>
  [
    record.status,
    record.smartCardId,
    record.externalId,
    record.cardId,
    record.reader,
    record.changedTimestamp,
    record.insertTimestamp,
    record.position,
    record.expireDate,
    record.client?.name,
  ]
    .filter((value) => value !== undefined && value !== null)
    .join(" ")
    .toLowerCase();

const TachographCardsList = () => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<TachographCardSorting>(DEFAULT_SORTING);
  const [search, setSearch] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editCardId, setEditCardId] = useState<string | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<TachographCardRecord | null>(null);

  const { message } = App.useApp();
  const { selectedDistributor } = useSelectDistributor();
  // const distributorId = getOptionalDistributorId(selectedDistributor);
  const distributorId = selectedDistributor ?? undefined;
  const formatEpoch = useEpochFormatter();

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      sort: sorting.sort,
      sortBy: sorting.sortBy,
      ...(distributorId ? { distributorId } : {}),
    }),
    [distributorId, pagination.page, pagination.size, sorting.sort, sorting.sortBy]
  );

  const listQuery = useQueryGetTachographCards(queryParams);
  const columnsQuery = useQueryGetTachographCardColumns();
  const deleteMutation = useMutationDeleteTachographCard();
  void columnsQuery.data;

  const response = listQuery.data?.data?.response;
  const records = useMemo(() => response?.resources ?? [], [response?.resources]);

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return records;

    return records.filter((record) => getSearchText(record).includes(normalizedSearch));
  }, [records, search]);

  const handleDeleteConfirm = async () => {
    if (!deleteRecord) return;

    try {
      await deleteMutation.mutateAsync(deleteRecord.cardId);
      message.success(t("tacho_card_deleted_successfully"));
      setDeleteRecord(null);
    } catch {
      // The shared API interceptor handles server-side delete errors.
    }
  };

  const openCreateForm = () => {
    setEditCardId(null);
    setIsFormOpen(true);
  };

  const openEditForm = useCallback((record: TachographCardRecord) => {
    setEditCardId(record.cardId);
    setIsFormOpen(true);
  }, []);

  const closeForm = () => {
    setIsFormOpen(false);
    setEditCardId(null);
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPagination((previous) => ({ ...previous, page: 0 }));
  }, []);

  const handleSortDirectionChange = useCallback((sort: TachographCardSortDirection) => {
    setSorting((previous) => ({ ...previous, sort }));
    setPagination((previous) => ({ ...previous, page: 0 }));
  }, []);

  const renderActions = useCallback(
    (record: TachographCardRecord): ReactNode => (
      <div className="flex items-center justify-start gap-2">
        <CustomTooltip title={t("edit")}>
          <CustomButton
            type="icon"
            buttonSize="small"
            aria-label={t("edit")}
            className="!bg-transparent"
            icon={<EditIconComponent className="h-4 w-4" />}
            onClick={(event) => {
              event.stopPropagation();
              openEditForm(record);
            }}
          />
        </CustomTooltip>

        <CustomTooltip title={t("delete")}>
          <CustomButton
            type="icon"
            buttonSize="small"
            aria-label={t("delete")}
            className="!bg-transparent"
            icon={<DeleteIconComponent className="h-4 w-4" />}
            onClick={(event) => {
              event.stopPropagation();
              setDeleteRecord(record);
            }}
          />
        </CustomTooltip>
      </div>
    ),
    [openEditForm, t]
  );

  const getSortOrder = useCallback(
    (columnId: string) => {
      if (sorting.sortBy !== columnId) return null;
      return sorting.sort === "ASC" ? "ascend" : "descend";
    },
    [sorting.sort, sorting.sortBy]
  );

  const columns: TableProps<TachographCardRecord>["columns"] = useMemo(
    () => [
      {
        title: t("connection"),
        key: "connection",
        onCell: () => ({ className: "!px-4" }),
        onHeaderCell: () => ({ className: "!px-4" }),
        render: (_, record) => (
          <TachographCardConnectionCell hardwareStatus={record.hardwareStatus} />
        ),
        width: 200,
      },
      {
        title: t("status"),
        key: "status",
        sorter: true,
        sortOrder: getSortOrder("STATUS"),
        width: 250,
        render: (_, record) => <TachographCardStatusCell status={record.status} />,
      },
      {
        title: t("card"),
        key: "card",
        sorter: true,
        width: 320,
        sortOrder: getSortOrder("CARD_ID"),
        render: (_, record) => {
          const fullCardId = record.cardId || "";
          const fullReader = record.reader || "";
          const smartCardId = record.smartCardId || "";
          const externalId = record.externalId || "";
          const halfCardId = fullCardId
            ? fullCardId.slice(Math.floor(fullCardId.length / 2))
            : "--";
          const displayReader =
            fullReader.length > 15 ? `${fullReader.slice(0, 15)}...` : fullReader;
          const displaySmartCardId =
            smartCardId.length > 12 ? `${smartCardId.slice(0, 12)}...` : smartCardId;
          const displayExternalId =
            externalId.length > 12 ? `${externalId.slice(0, 12)}...` : externalId;

          return (
            <div className="flex flex-col gap-1 py-2 text-sm">
              <span>
                <strong className="font-[600] leading-[18px]">{t("smart_card_id")}:</strong>{" "}
                <CustomTooltip title={smartCardId.length > 12 ? smartCardId : ""}>
                  <span className="leading-[18px]">{displaySmartCardId || "--"}</span>
                </CustomTooltip>
              </span>
              <span>
                <strong className="font-[600] leading-[18px]">{t("external_id")}:</strong>{" "}
                <CustomTooltip title={externalId.length > 12 ? externalId : ""}>
                  <span className="leading-[18px]">{displayExternalId || "--"}</span>
                </CustomTooltip>
              </span>
              <span className="max-w-[190px] truncate">
                <strong className="font-[600] leading-[18px]">{t("card_id")}:</strong>{" "}
                <CustomTooltip title={fullCardId}>
                  <span className="leading-[18px]">{halfCardId}</span>
                </CustomTooltip>
              </span>
              <span>
                <strong className="font-[600] leading-[18px]">{t("reader")}:</strong>{" "}
                <CustomTooltip title={fullReader.length > 15 ? fullReader : ""}>
                  <span className="leading-[18px]">{displayReader || "--"}</span>
                </CustomTooltip>
              </span>
            </div>
          );
        },
      },
      {
        title: t("changed_timestamp"),
        key: "changedTimestamp",
        sorter: true,
        sortOrder: getSortOrder("CHANGED_TIMESTAMP"),
        render: (_, record) => (
          <span className="text-[14px] leading-[18px]">
            {record.changedTimestamp ? timeAgo(record.changedTimestamp, t) : "--"}
          </span>
        ),
      },
      {
        title: t("insert_timestamp"),
        key: "insertTimestamp",
        sorter: true,
        sortOrder: getSortOrder("INSERT_TIMESTAMP"),
        render: (_, record) => (
          <span className="text-[14px] leading-[18px]">
            {record.insertTimestamp ? formatEpoch(record.insertTimestamp, "HH:mm:ss") : "--"}
          </span>
        ),
      },
      {
        title: t("card_position"),
        key: "position",
        sorter: true,
        sortOrder: getSortOrder("CARD_POSITION"),
        render: (_, record) => (
          <span className="text-[14px] font-[600] leading-[18px]">{record.position ?? "--"}</span>
        ),
      },
      {
        title: t("expire_date"),
        key: "expireDate",
        sorter: true,
        sortOrder: getSortOrder("EXPIRE_DATE"),
        render: (_, record) => {
          if (!record?.expireDate) return "--";
          return (
            <span className="text-[14px] leading-[18px]">
              <EpochDate date={record.expireDate} />
            </span>
          );
        },
      },
      {
        title: t("customer"),
        key: "customer",
        sorter: true,
        sortOrder: getSortOrder("CUSTOMER"),
        render: (_, record) => (
          <span className="text-[14px] leading-[18px]">
            <b>{record.client?.name || "--"}</b>
          </span>
        ),
      },
      {
        title: t("actions"),
        key: "actions",
        align: "start",
        render: (_, record) => renderActions(record),
      },
    ],
    [formatEpoch, getSortOrder, renderActions, t]
  );

  const handleTableChange: TableProps<TachographCardRecord>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action === "filter") return;

    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const key = currentSorter?.columnKey ?? currentSorter?.field;
    const sortBy = key ? COLUMN_IDS[String(key)] : undefined;

    setSorting(
      currentSorter?.order && sortBy
        ? {
            sortBy,
            sort: currentSorter.order === "ascend" ? "ASC" : "DESC",
          }
        : DEFAULT_SORTING
    );
    setPagination((previous) => ({ ...previous, page: 0 }));
  };

  return (
    <section className="min-h-full bg-gray1 pb-8 pl-2">
      <CustomSharedTitleSection
        titleKey="tachograph_cards"
        showBtn={false}
        onClick={openCreateForm}
        titleClassName="!text-[25.23px] !font-[700] !leading-[28px] !mb-1.5"
      />

      <div className="flex flex-col overflow-hidden rounded-[16px] bg-white">
        <TachographCardsToolbar
          search={search}
          sortDirection={sorting.sort}
          disabled={listQuery.isLoading}
          onSearchChange={handleSearchChange}
          onSortDirectionChange={handleSortDirectionChange}
        />

        <CustomTable<TachographCardRecord>
          data={filteredRecords}
          columns={columns}
          customClass="customers-table tachograph-cards-table [&_.ant-table-tbody>tr]:min-h-[96px] [&_.ant-table-tbody>tr>td]:!py-2"
          rowKey={(record) => record.cardId}
          isLoading={listQuery.isLoading || listQuery.isFetching}
          pageSize={pagination.size}
          currentPage={pagination.page}
          totalCount={
            search.trim() ? filteredRecords.length : getTotalCount(response, records.length)
          }
          handlePagination={(page) => setPagination((previous) => ({ ...previous, page }))}
          handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
          showSearchInput={false}
          showCustomizeColumns={false}
          showDownload={false}
          onChange={handleTableChange}
          sortDirections={["ascend", "descend", "ascend"]}
        />
      </div>

      <TachographCardAddEdit open={isFormOpen} cardId={editCardId} onClose={closeForm} />

      <TachographCardDelete
        open={Boolean(deleteRecord)}
        isLoading={deleteMutation.isPending}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDeleteConfirm}
      />
    </section>
  );
};

export default TachographCardsList;
